import React, { useMemo, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

export const calcDiscount = (original, promo) => {
  const o = parseFloat(original);
  const p = parseFloat(promo);
  if (!o || !p || p >= o) return null;
  return Math.round(((o - p) / o) * 1000) / 10;
};

const ItemRow = ({ item, product, onUpdate, onRemove }) => {
  const discount = calcDiscount(item.original_price, item.promo_price);
  const invalid = item.promo_price !== '' && item.promo_price !== undefined && discount === null;
  return (
    <div data-testid={`promo-item-${item.product_id}`} className="grid grid-cols-12 gap-3 items-center py-3 border-b border-border last:border-0">
      <div className="col-span-12 sm:col-span-4 flex items-center gap-3 min-w-0">
        <img src={product?.images?.[0] || '/placeholder.svg'} alt="" className="w-10 h-10 object-contain bg-neutral-50 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm truncate">{product?.name || 'Produto removido'}</p>
          {(item.sku || product?.sku) && <p className="text-[11px] text-muted-foreground">SKU {item.sku || product?.sku}</p>}
        </div>
      </div>
      <label className="col-span-5 sm:col-span-3 text-[10px] uppercase tracking-widest text-muted-foreground">
        Preço anterior
        <input
          type="number" step="0.01" min="0"
          data-testid={`promo-item-original-${item.product_id}`}
          value={item.original_price}
          onChange={(e) => onUpdate({ original_price: e.target.value })}
          className="mt-1 w-full bg-transparent border-b border-border focus:border-primary py-1 text-sm text-foreground outline-none"
        />
      </label>
      <label className="col-span-5 sm:col-span-3 text-[10px] uppercase tracking-widest text-muted-foreground">
        Preço promocional
        <input
          type="number" step="0.01" min="0" required
          data-testid={`promo-item-price-${item.product_id}`}
          value={item.promo_price}
          onChange={(e) => onUpdate({ promo_price: e.target.value })}
          className={`mt-1 w-full bg-transparent border-b py-1 text-sm text-foreground outline-none ${invalid ? 'border-red-500' : 'border-border focus:border-primary'}`}
        />
      </label>
      <div className="col-span-2 sm:col-span-2 flex items-center justify-end gap-2">
        <span data-testid={`promo-item-discount-${item.product_id}`} className={`text-xs font-semibold px-2 py-1 ${discount ? 'bg-red-600 text-white' : 'text-muted-foreground'}`}>
          {discount ? `-${discount}%` : '—'}
        </span>
        <button type="button" onClick={onRemove} data-testid={`promo-item-remove-${item.product_id}`} className="p-1 text-muted-foreground hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const PromotionItemsEditor = ({ products, items, onChange }) => {
  const [query, setQuery] = useState('');
  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const selected = new Set(items.map((i) => i.product_id));
  const q = query.trim().toLowerCase();
  const filtered = products.filter((p) => !q || p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q));

  const toggle = (p) => {
    if (selected.has(p.id)) onChange(items.filter((i) => i.product_id !== p.id));
    else onChange([...items, { product_id: p.id, sku: p.sku || '', original_price: p.price, promo_price: '' }]);
  };
  const update = (pid, patch) => onChange(items.map((i) => (i.product_id === pid ? { ...i, ...patch } : i)));

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-0 top-3 text-muted-foreground" />
        <input
          data-testid="promo-product-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome ou SKU..."
          className="w-full bg-transparent border-b border-border focus:border-primary pl-6 py-2 text-sm outline-none"
        />
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 max-h-56 overflow-y-auto border border-border p-3">
        {filtered.map((p) => (
          <button
            type="button" key={p.id} onClick={() => toggle(p)}
            data-testid={`promo-product-toggle-${p.id}`}
            className={`text-left border-2 p-2 transition-colors ${selected.has(p.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
          >
            <img src={p.images?.[0] || '/placeholder.svg'} alt={p.name} className="w-full aspect-square object-contain mb-1" />
            <p className="text-xs truncate">{p.name}</p>
            <p className="text-[10px] text-muted-foreground">R$ {brl(p.price)}</p>
          </button>
        ))}
      </div>
      <div data-testid="promo-items-list" className="border border-border px-4">
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Selecione produtos acima ou importe uma planilha.</p>
        ) : (
          items.map((it) => (
            <ItemRow key={it.product_id} item={it} product={byId[it.product_id]}
              onUpdate={(patch) => update(it.product_id, patch)}
              onRemove={() => onChange(items.filter((i) => i.product_id !== it.product_id))} />
          ))
        )}
      </div>
      <p className="text-xs text-muted-foreground">{items.length} produto(s) com preço individual</p>
    </div>
  );
};

export default PromotionItemsEditor;
